import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { format, formatDistanceToNow } from "date-fns";
import {
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  Database,
  RefreshCw,
  Server,
  Shield,
  Sparkles,
  Trash2,
  Bug,
  Globe,
  Inbox,
  XCircle,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

type Status = "ok" | "warn" | "error";

interface ServiceCheck {
  status: Status;
  message?: string;
  configured?: boolean;
  latencyMs?: number;
  callsLast24h?: number;
  successRate?: number | null;
  lastSuccessAt?: string | null;
  commit?: string;
  nodeVersion?: string;
  uptimeSeconds?: number;
  projectId?: string | null;
  environment?: string;
  release?: string | null;
}

interface QueueSnapshotByType {
  active: number;
  errored: number;
}

interface SystemHealth {
  checkedAt: string;
  windowHours: number;
  services: {
    database: ServiceCheck;
    web: ServiceCheck;
    openai: ServiceCheck;
    tavily: ServiceCheck;
    firebase: ServiceCheck;
    sentry: ServiceCheck;
  };
  queue: {
    status: Status;
    active: number;
    errored: number;
    profile: QueueSnapshotByType;
    tapscan: QueueSnapshotByType;
    generic: QueueSnapshotByType;
    message?: string;
  };
}

interface RecentErrorLog {
  id: string;
  endpoint: string;
  method: string;
  statusCode: number;
  duration: number;
  errorMessage?: string;
  createdAt: string;
}

export function ApiStatus() {
  const { toast } = useToast();

  // One-shot service health check. No polling — only fires when the page mounts
  // or the admin clicks "Refresh". Each load makes one backend round-trip.
  const {
    data: health,
    isLoading: healthLoading,
    isFetching: healthFetching,
    refetch: refetchHealth,
  } = useQuery<SystemHealth>({
    queryKey: ["/api/admin/system-health"],
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  // Recent errors. Small list, on-demand. Sentry has the full picture; this is
  // a convenience peek for admins who don't want to leave the app.
  const {
    data: recentErrors,
    refetch: refetchErrors,
  } = useQuery<RecentErrorLog[]>({
    queryKey: ["/api/admin/api-logs", "recent-errors"],
    queryFn: async () => {
      const params = new URLSearchParams({
        statusFilter: "error",
        limit: "15",
      });
      return apiRequest(`/api/admin/api-logs?${params.toString()}`, "GET") as Promise<RecentErrorLog[]>;
    },
    refetchInterval: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const clearLogsMutation = useMutation({
    mutationFn: async () => apiRequest("/api/admin/api-logs", "DELETE"),
    onSuccess: () => {
      toast({ title: "Logs cleared" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/api-logs"] });
    },
    onError: (err: any) => {
      toast({
        title: "Failed to clear logs",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      });
    },
  });

  const refreshAll = () => {
    refetchHealth();
    refetchErrors();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold">API Status</h2>
          <p className="text-gray-500">
            One-shot health check of every service the toolkit depends on. Run on page load —
            click refresh to re-check. Full error stream lives in Sentry.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={refreshAll} variant="outline" size="icon" disabled={healthFetching}>
            <RefreshCw className={`h-4 w-4 ${healthFetching ? "animate-spin" : ""}`} />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="icon" title="Clear api_logs">
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear all API logs?</AlertDialogTitle>
                <AlertDialogDescription>
                  Deletes every row in the api_logs table. The daily retention cron will keep
                  pruning normally afterward. This is mainly useful if you want a clean slate
                  for forensic work.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => clearLogsMutation.mutate()}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Clear all logs
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Infrastructure (static description of what runs where) */}
      <InfrastructurePanel />

      {/* Live service health */}
      <ServiceHealthCard health={health} loading={healthLoading} />

      {/* Queue snapshot — only matters when admins have run a batch */}
      <QueueSnapshotCard queue={health?.queue} />

      {/* Recent errors (forensic peek) */}
      <RecentErrorsCard errors={recentErrors ?? []} />
    </div>
  );
}

function statusBadge(status: Status | undefined) {
  if (status === "ok") {
    return (
      <Badge className="bg-green-600 hover:bg-green-700 gap-1">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Healthy
      </Badge>
    );
  }
  if (status === "warn") {
    return (
      <Badge className="bg-amber-500 hover:bg-amber-600 gap-1">
        <AlertTriangle className="h-3.5 w-3.5" />
        Warning
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="destructive" className="gap-1">
        <XCircle className="h-3.5 w-3.5" />
        Down
      </Badge>
    );
  }
  return <Badge variant="outline">Unknown</Badge>;
}

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return "never";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch {
    return "—";
  }
}

function ServiceHealthCard({ health, loading }: { health: SystemHealth | undefined; loading: boolean }) {
  if (loading || !health) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Service health</CardTitle>
          <CardDescription>Pinging dependencies…</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-sm text-gray-500">Checking…</div>
        </CardContent>
      </Card>
    );
  }

  const { services, checkedAt, windowHours } = health;

  // Order by "most likely to be broken if anything is broken" so the eye lands
  // on outages first.
  const rows: Array<{
    icon: React.ComponentType<{ className?: string }>;
    name: string;
    check: ServiceCheck;
    detail: React.ReactNode;
  }> = [
    {
      icon: Database,
      name: "Supabase Postgres",
      check: services.database,
      detail: services.database.status === "ok"
        ? <>Ping returned in <span className="font-mono">{services.database.latencyMs} ms</span>.</>
        : services.database.message ?? "Ping failed.",
    },
    {
      icon: Server,
      name: "Render Web Service",
      check: services.web,
      detail: (
        <>
          Commit <span className="font-mono">{(services.web.commit ?? "unknown").slice(0, 7)}</span>{" "}
          on Node {services.web.nodeVersion}. Up for{" "}
          {Math.round((services.web.uptimeSeconds ?? 0) / 60)} min.
        </>
      ),
    },
    {
      icon: Sparkles,
      name: "OpenAI",
      check: services.openai,
      detail: services.openai.configured
        ? services.openai.callsLast24h && services.openai.callsLast24h > 0
          ? <>
              {services.openai.callsLast24h} calls in {windowHours}h,{" "}
              {Math.round((services.openai.successRate ?? 0) * 100)}% success. Last successful call{" "}
              {relativeTime(services.openai.lastSuccessAt)}.
            </>
          : <>API key configured. No calls in the last {windowHours}h.</>
        : "OPENAI_API_KEY is not set.",
    },
    {
      icon: Globe,
      name: "Tavily web search",
      check: services.tavily,
      detail: services.tavily.configured
        ? services.tavily.callsLast24h && services.tavily.callsLast24h > 0
          ? <>
              Web search invoked {services.tavily.callsLast24h} times in {windowHours}h. Last{" "}
              {relativeTime(services.tavily.lastSuccessAt)}.
            </>
          : <>API key configured. No VBR runs in the last {windowHours}h.</>
        : "TAVILY_API_KEY is not set.",
    },
    {
      icon: Shield,
      name: "Firebase Auth",
      check: services.firebase,
      detail: services.firebase.configured
        ? <>Service account loaded for project <span className="font-mono">{services.firebase.projectId}</span>.</>
        : "Service account or project ID missing — logins will fail.",
    },
    {
      icon: Bug,
      name: "Sentry",
      check: services.sentry,
      detail: services.sentry.configured
        ? <>Reporting to environment <span className="font-mono">{services.sentry.environment}</span>, release <span className="font-mono">{(services.sentry.release ?? "unknown").slice(0, 7)}</span>.</>
        : "SENTRY_DSN missing — server-side errors will not be reported.",
    },
  ];

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Service health</CardTitle>
          <CardDescription>
            Checked {relativeTime(checkedAt)}. Click refresh up top to re-run.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {rows.map(({ icon: Icon, name, check, detail }) => (
            <div key={name} className="py-3 flex items-start gap-3">
              <Icon className="h-5 w-5 mt-0.5 shrink-0 text-gray-500" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium text-sm">{name}</span>
                  {statusBadge(check.status)}
                </div>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{detail}</p>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function QueueSnapshotCard({ queue }: { queue: SystemHealth["queue"] | undefined }) {
  if (!queue) return null;
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div className="flex items-center gap-2">
          <Inbox className="h-5 w-5 text-gray-500" />
          <div>
            <CardTitle className="text-base">OpenAI Batch queue</CardTitle>
            <CardDescription>
              Live count of Pipeline B items. Should be 0 active most of the year.
            </CardDescription>
          </div>
        </div>
        {statusBadge(queue.status)}
      </CardHeader>
      <CardContent>
        {queue.message ? (
          <p className="text-sm text-red-600">{queue.message}</p>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            {(["profile", "tapscan", "generic"] as const).map(kind => (
              <div key={kind} className="rounded border p-3">
                <div className="text-xs uppercase tracking-wide text-gray-500">{kind}</div>
                <div className="mt-1 flex items-baseline gap-2">
                  <span className="text-2xl font-semibold">{queue[kind].active}</span>
                  <span className="text-xs text-gray-500">active</span>
                </div>
                {queue[kind].errored > 0 && (
                  <div className="mt-1 text-xs text-red-600">
                    {queue[kind].errored} errored
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function RecentErrorsCard({ errors }: { errors: RecentErrorLog[] }) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Recent errors</CardTitle>
            <CardDescription>
              Quick peek at recent failing API calls (4xx / 5xx). Full stack traces and
              breadcrumbs are in Sentry.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {errors.length === 0 ? (
          <p className="text-sm text-gray-500">No recent errors. 🎉</p>
        ) : (
          <div className="divide-y">
            {errors.map(e => (
              <div key={e.id} className="py-2 flex items-start gap-3 text-sm">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0 text-red-500" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-xs truncate">
                    <span className="font-semibold mr-2">{e.method}</span>
                    <span className="text-gray-700 dark:text-gray-300">{e.endpoint}</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {e.statusCode} • {e.duration}ms • {format(new Date(e.createdAt), "MMM d, HH:mm:ss")}
                    {e.errorMessage ? <span className="ml-2 italic">— {e.errorMessage}</span> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Static infrastructure panel. Same content as before — describes the platform,
 * separate from the on-demand health probe.
 */
function InfrastructurePanel() {
  const commitSha = (import.meta.env.VITE_RENDER_GIT_COMMIT as string | undefined) || "";
  const shortSha = commitSha ? commitSha.slice(0, 7) : "unknown";
  const commitHref = commitSha
    ? `https://github.com/Market-Enginuity/ai-toolkit-v2/commit/${commitSha}`
    : "";

  const rows: Array<{
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: React.ReactNode;
  }> = [
    {
      icon: Server,
      label: "Hosting",
      value: (
        <>
          <span className="font-medium">Render</span> (Virginia / US East). One Web Service
          (<code className="text-xs">ai-toolkit-v2</code>) plus two Cron Jobs: a 5-minute
          OpenAI Batch queue poller and a daily <code className="text-xs">api_logs</code>{" "}
          retention prune.
        </>
      ),
    },
    {
      icon: Database,
      label: "Database",
      value: (
        <>
          <span className="font-medium">Supabase</span> PostgreSQL 17 with the{" "}
          <code className="text-xs">pgvector</code> extension for embedding similarity
          search. Connected via the Session Pooler.
        </>
      ),
    },
    {
      icon: Shield,
      label: "Authentication",
      value: (
        <>
          <span className="font-medium">Firebase Authentication</span> (email/password +
          Google Sign-In). Server verifies ID tokens with{" "}
          <code className="text-xs">checkRevoked: true</code> so disabled users lose access
          within one request.
        </>
      ),
    },
    {
      icon: Sparkles,
      label: "AI & search",
      value: (
        <>
          <span className="font-medium">OpenAI</span> (GPT-5 family with automatic
          fallback to GPT-5-mini and GPT-4o) for generation, plus the OpenAI Batch API for
          the twice-yearly fact-processing queue.{" "}
          <span className="font-medium">Tavily</span> handles real-time web search during
          VBR generation.
        </>
      ),
    },
    {
      icon: Bug,
      label: "Error tracking",
      value: (
        <>
          <span className="font-medium">Sentry</span> captures server, worker, and React
          errors. Source maps uploaded at build time; PII is scrubbed from request bodies
          and session replays mask all text.
        </>
      ),
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Infrastructure</CardTitle>
        <CardDescription>
          Where the AI Sales Toolkit actually runs. If something is broken at the platform
          level, this is where to look first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="space-y-3">
          {rows.map(({ icon: Icon, label, value }) => (
            <div key={label} className="flex gap-3">
              <Icon className="h-5 w-5 mt-0.5 shrink-0 text-gray-500" />
              <div className="flex-1">
                <dt className="text-sm font-medium">{label}</dt>
                <dd className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">{value}</dd>
              </div>
            </div>
          ))}
        </dl>
        <div className="mt-4 pt-3 border-t text-xs text-gray-500 flex items-center justify-between">
          <span>Source code: github.com/Market-Enginuity/ai-toolkit-v2</span>
          {commitHref ? (
            <a
              href={commitHref}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:underline font-mono"
            >
              Deployed commit: {shortSha}
            </a>
          ) : (
            <span className="font-mono">Deployed commit: {shortSha}</span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
